import ExcelJS from 'exceljs';
import activoClassificationService from '../services/activoClassificationService';
import { getAreas } from '../services/areaService';
import activoService from '../services/activoService';
import {
  cellText,
  summarizeImportResult,
} from './classificationExcel';

export { summarizeImportResult };

export const ACTIVO_INICIAL_COL = {
  code: 'Código',
  name: 'Nombre',
  categoryRef: 'Categoría (código o nombre)',
  subRef: 'Subcategoría (código o nombre)',
  familyRef: 'Familia (código o nombre)',
  areaRef: 'Área (código o nombre)',
  quantity: 'Cantidad',
  unitPrice: 'Precio unitario sin IGV',
  activityStart: 'Fecha inicio actividades',
  usefulLife: 'Vida útil (años)',
  brand: 'Marca',
  model: 'Modelo',
  serial: 'N° serie',
  notes: 'Notas',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

const REQUIRED = [
  ACTIVO_INICIAL_COL.code,
  ACTIVO_INICIAL_COL.name,
  ACTIVO_INICIAL_COL.categoryRef,
  ACTIVO_INICIAL_COL.subRef,
  ACTIVO_INICIAL_COL.familyRef,
  ACTIVO_INICIAL_COL.areaRef,
  ACTIVO_INICIAL_COL.unitPrice,
  ACTIVO_INICIAL_COL.activityStart,
  ACTIVO_INICIAL_COL.usefulLife,
];

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = HEADER_FILL;
  });
}

export async function downloadActivosInicialesTemplate() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Activos iniciales', { views: [{ state: 'frozen', ySplit: 2 }] });
  const headers = Object.values(ACTIVO_INICIAL_COL);
  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);

  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value =
    'Carga masiva de activos que ya existían antes de usar el sistema. Fila 2 = encabezados. Datos desde fila 3. '
    + 'Indique clasificación y área como en el maestro (código o nombre). '
    + 'Fecha inicio actividades: DD/MM/AAAA o AAAA-MM-DD. '
    + 'Cantidad opcional (por defecto 1). Precio unitario sin IGV obligatorio.';
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 72;
  styleHeaderRow(ws.getRow(2), headers);
  ws.columns = [
    { width: 16 }, { width: 32 }, { width: 24 }, { width: 24 }, { width: 24 },
    { width: 20 }, { width: 10 }, { width: 18 }, { width: 20 }, { width: 14 },
    { width: 14 }, { width: 14 }, { width: 14 }, { width: 28 },
  ];

  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, 'plantilla_activos_iniciales.xlsx');
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet, requiredLabels) {
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const row = sheet.getRow(r);
    const map = headerMapFromRow(row);
    if (requiredLabels.every((label) => map[label] != null)) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

function findByRef(items, ref, parentFilter = null) {
  const q = String(ref || '').trim();
  if (!q) return null;
  const lower = q.toLowerCase();
  const pool = parentFilter ? items.filter(parentFilter) : items;
  let match = pool.find((x) => String(x.code).toLowerCase() === lower);
  if (match) return match;
  match = pool.find((x) => String(x.name).trim().toLowerCase() === lower);
  return match || null;
}

function isActiveRecord(item) {
  return item && item.is_active !== false;
}

function parseDateCell(raw, cell) {
  if (cell?.value instanceof Date) {
    const d = cell.value;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return { ok: true, value: `${y}-${m}-${day}` };
  }

  const text = String(raw || '').trim();
  if (!text) return { ok: false, message: 'Fecha de inicio requerida' };

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return { ok: true, value: text };
  }

  const m = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const day = m[1].padStart(2, '0');
    const month = m[2].padStart(2, '0');
    return { ok: true, value: `${m[3]}-${month}-${day}` };
  }

  return { ok: false, message: 'Fecha inválida (use DD/MM/AAAA)' };
}

function parseQuantity(raw) {
  if (raw == null || raw === '') return { ok: true, value: 1 };
  const n = parseFloat(String(raw).replace(',', '.'));
  if (Number.isNaN(n) || n <= 0) return { ok: false, message: 'Cantidad inválida' };
  return { ok: true, value: n };
}

function parsePrice(raw) {
  if (raw == null || raw === '') return { ok: false, message: 'Precio unitario requerido' };
  const n = parseFloat(String(raw).replace(',', '.'));
  if (Number.isNaN(n) || n < 0) return { ok: false, message: 'Precio unitario inválido' };
  return { ok: true, value: n };
}

function parseUsefulLife(raw) {
  if (raw == null || raw === '') return { ok: false, message: 'Vida útil requerida' };
  const n = parseInt(String(raw).trim(), 10);
  if (Number.isNaN(n) || n < 1 || n > 100) return { ok: false, message: 'Vida útil inválida (1-100)' };
  return { ok: true, value: n };
}

function rowHasData(row, colMap) {
  return REQUIRED.some((label) => {
    const col = colMap[label];
    return col && cellText(row.getCell(col));
  });
}

export async function analyzeActivosInicialesImport(file) {
  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findHeaderRow(sheet, REQUIRED);
  if (!found) {
    return {
      success: false,
      fatalMessage: `Faltan encabezados obligatorios. Debe existir: ${REQUIRED.join(', ')}.`,
    };
  }

  const [catRes, subRes, famRes, areasRes, activosRes] = await Promise.all([
    activoClassificationService.getCategories({ active_only: true }),
    activoClassificationService.getSubcategories({ active_only: true }),
    activoClassificationService.getFamilies({ active_only: true }),
    getAreas({ active_only: true }),
    activoService.getActivos(),
  ]);

  if (!catRes.success || !subRes.success || !famRes.success || !areasRes.success) {
    return { success: false, fatalMessage: 'No se pudieron cargar clasificación o áreas.' };
  }

  const categories = catRes.data || [];
  const subcategories = subRes.data || [];
  const families = famRes.data || [];
  const areas = areasRes.data || [];
  const existingCodes = new Set(
    (activosRes.success ? activosRes.data || [] : []).map((a) => String(a.code || '').trim().toLowerCase()),
  );

  const { rowIndex, colMap } = found;
  const lastRow = getLastUsedRow(sheet);
  const previewRows = [];
  const validTasks = [];
  const seenCodes = new Set();

  for (let r = rowIndex + 1; r <= lastRow; r++) {
    const row = sheet.getRow(r);
    if (!rowHasData(row, colMap)) continue;

    const code = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.code])).trim();
    const name = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.name])).trim();
    const categoryRef = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.categoryRef]));
    const subRef = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.subRef]));
    const familyRef = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.familyRef]));
    const areaRef = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.areaRef]));
    const qtyCell = colMap[ACTIVO_INICIAL_COL.quantity]
      ? row.getCell(colMap[ACTIVO_INICIAL_COL.quantity])
      : null;
    const priceRaw = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.unitPrice]));
    const startCell = row.getCell(colMap[ACTIVO_INICIAL_COL.activityStart]);
    const startRaw = cellText(startCell);
    const lifeRaw = cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.usefulLife]));

    const issues = [];
    if (!code) issues.push('Código requerido');
    if (!name) issues.push('Nombre requerido');
    if (code && seenCodes.has(code.toLowerCase())) issues.push('Código duplicado en el archivo');
    if (code && existingCodes.has(code.toLowerCase())) issues.push('El código ya existe en el sistema');
    if (code) seenCodes.add(code.toLowerCase());

    const category = findByRef(categories, categoryRef);
    if (!categoryRef.trim()) issues.push('Categoría requerida');
    else if (!category) issues.push('Categoría no encontrada o inactiva');
    const subcategory = category
      ? findByRef(subcategories, subRef, (s) => Number(s.category_id) === Number(category.id))
      : null;
    if (category && !subRef.trim()) issues.push('Subcategoría requerida');
    else if (category && !subcategory) issues.push('Subcategoría no encontrada o inactiva');
    const family = subcategory
      ? findByRef(families, familyRef, (f) => Number(f.subcategory_id) === Number(subcategory.id))
      : null;
    if (subcategory && !familyRef.trim()) issues.push('Familia requerida');
    else if (subcategory && !family) issues.push('Familia no encontrada o inactiva');
    else if (family && !isActiveRecord(family)) issues.push('La familia no está activa');
    const area = findByRef(areas, areaRef);
    if (!areaRef.trim()) issues.push('Área requerida');
    else if (!area) issues.push('Área no encontrada o inactiva');
    else if (!isActiveRecord(area)) issues.push('El área no está activa');

    const qtyParsed = parseQuantity(qtyCell ? cellText(qtyCell) : '');
    if (!qtyParsed.ok) issues.push(qtyParsed.message);
    const priceParsed = parsePrice(priceRaw);
    if (!priceParsed.ok) issues.push(priceParsed.message);
    const dateParsed = parseDateCell(startRaw, startCell);
    if (!dateParsed.ok) issues.push(dateParsed.message);
    const lifeParsed = parseUsefulLife(lifeRaw);
    if (!lifeParsed.ok) issues.push(lifeParsed.message);

    const brand = colMap[ACTIVO_INICIAL_COL.brand]
      ? cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.brand])).trim()
      : '';
    const model = colMap[ACTIVO_INICIAL_COL.model]
      ? cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.model])).trim()
      : '';
    const serial = colMap[ACTIVO_INICIAL_COL.serial]
      ? cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.serial])).trim()
      : '';
    const notes = colMap[ACTIVO_INICIAL_COL.notes]
      ? cellText(row.getCell(colMap[ACTIVO_INICIAL_COL.notes])).trim()
      : '';

    const status = issues.length ? 'error' : 'valid';
    previewRows.push({
      excelRow: r,
      status,
      cols: {
        codigo: code || '—',
        nombre: name || '—',
        clasificacion: family ? `${category?.name} › ${subcategory?.name} › ${family.name}` : '—',
        area: area?.name || areaRef || '—',
        costo: priceParsed.ok ? priceParsed.value * qtyParsed.value : '—',
        inicio: dateParsed.ok ? dateParsed.value : startRaw || '—',
        vidaUtil: lifeParsed.ok ? `${lifeParsed.value} años` : lifeRaw || '—',
      },
      issue: issues.join('; '),
    });

    if (status === 'valid') {
      validTasks.push({
        excelRow: r,
        payload: {
          code,
          name,
          activo_family_id: family.id,
          area_id: area.id,
          acquisition_quantity: qtyParsed.value,
          acquisition_unit_price: priceParsed.value,
          acquisition_currency: 'PEN',
          activity_start_date: dateParsed.value,
          useful_life_years: lifeParsed.value,
          brand: brand || null,
          model: model || null,
          serial_number: serial || null,
          notes: notes || null,
        },
      });
    }
  }

  if (previewRows.length === 0) {
    return { success: false, fatalMessage: 'No se encontraron filas con datos.' };
  }

  return {
    success: true,
    previewRows,
    validTasks,
    validCount: validTasks.length,
    errorCount: previewRows.filter((x) => x.status === 'error').length,
  };
}

export async function executeActivosInicialesImport(validTasks) {
  if (!validTasks.length) {
    return { created: 0, errors: [{ row: 0, message: 'No hay filas válidas para importar.' }] };
  }

  const response = await activoService.importInitialActivos({
    items: validTasks.map((t) => ({
      ...t.payload,
      excel_row: t.excelRow,
    })),
  });

  if (!response.success) {
    return {
      created: 0,
      errors: [{ row: 0, message: response.message || 'Error al importar activos' }],
    };
  }

  const errors = (response.errors || []).map((e) => ({
    row: e.row,
    message: e.message,
    code: e.code,
  }));

  return {
    created: response.created || 0,
    errors,
    message: response.message,
  };
}

export async function importActivosInicialesFromExcel(file) {
  const analysis = await analyzeActivosInicialesImport(file);
  if (!analysis.success) {
    return { created: 0, errors: [{ row: 0, message: analysis.fatalMessage }] };
  }
  if (!analysis.validTasks.length) {
    return {
      created: 0,
      errors: analysis.previewRows
        .filter((x) => x.status === 'error')
        .map((x) => ({ row: x.excelRow, message: x.issue })),
    };
  }
  return executeActivosInicialesImport(analysis.validTasks);
}
